import { IconButton, InputAdornment, Typography } from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import React, { ReactElement, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Control, FieldError } from 'react-hook-form/dist/types';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import { useTranslation } from 'react-i18next';

import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { StyledTextField } from 'components/InputField';
import { FieldErrorText, InputContainer, TitleContainer } from './RHF.styles';

type RHFTextFieldProps = {
  name: string;
  control: Control;
  label: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  addons?: ReactElement;
  error?: FieldError;
  rules?: RegisterOptions;
  asyncValidate?: (value: string) => Promise<string | undefined>;
  autoComplete?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
};

/**
 * Champ texte compatible React Hook Form v6.
 * Reprend le style de InputFieldWithLabel existant.
 */
const RHFTextField = ({
  name,
  control,
  label,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  addons,
  error,
  rules = {},
  asyncValidate,
  autoComplete,
  inputMode,
}: RHFTextFieldProps): JSX.Element => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === 'password';
  const resolvedType = isPasswordField && showPassword ? 'text' : type;

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Fusionner les validations synchrones (rules) et asynchrone (asyncValidate)
  const mergedRules: RegisterOptions = {
    ...rules,
    validate: {
      ...(typeof rules.validate === 'object' ? rules.validate : {}),
      ...(asyncValidate
        ? {
            async: async (value: string) => {
              const result = await asyncValidate(value);
              return result || true;
            },
          }
        : {}),
    },
  };

  return (
    <InputContainer>
      <TitleContainer>
        <LabelWithUIGuidelines label={label} required={required} />
        {addons}
      </TitleContainer>
      <Controller
        name={name}
        control={control}
        rules={mergedRules}
        render={({ onChange, onBlur, value, ref }) => (
          <StyledTextField
            inputRef={ref}
            fullWidth
            type={resolvedType}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            error={!!error}
            inputProps={{
              name,
              ...(autoComplete ? { autoComplete } : {}),
              ...(inputMode ? { inputMode } : {}),
            }}
            InputProps={
              isPasswordField
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword
                              ? t('auth.global.hide_password')
                              : t('auth.global.show_password')
                          }
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        )}
      />
      {error && (
        <FieldErrorText>
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        </FieldErrorText>
      )}
    </InputContainer>
  );
};

export default RHFTextField;
